import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ExternalLink, Mail, Phone } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CompactRecordList,
  CompactRecordListItem,
  CollectionViewToggle,
  DetailSection,
  Pagination,
  RecordCard,
  RecordCardGrid,
  RecordDetailHeading,
  RecordDetailShell,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  RecordTabs,
  ListToolbar,
  SearchField,
  WorkspaceSplitView,
  useCollectionView,
  useIsSplitView,
} from '@command-center/ui-system';
import { ActivityTimeline, DetailTabBody, SimpleNotesPanel, RelatedRecordList } from '../components/record-detail-panels';
import { leadloopApi, type CascadeOrganizationListItem } from '../lib/leadloop-api';
import {
  useLoadedRecordDetails,
  type RightSidebarRecordDetails,
} from '@command-center/oneup-portal-ui';
import { EmailLink } from './SendEmailDialog';
import { usePagedView } from '../lib/use-paged-view';
import {
  Cell,
  CountCell,
  DateCell,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  StatusBadge,
  formatDateTime,
} from './parts';

export function OrganizationsView() {
  const [q, setQ] = useState('');
  const [view, setView] = useCollectionView('cascade:records:organizations:view');
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { page, setPage, pageSize } = usePagedView([q]);
  const organizations = useQuery({
    queryKey: ['organizations', q, page, pageSize],
    queryFn: () => leadloopApi.organizations(q || undefined, { page, pageSize }),
    placeholderData: (previous) => previous,
  });
  const items = useMemo(() => organizations.data?.items ?? [], [organizations.data?.items]);
  const selectedIndex = id ? items.findIndex((item) => item.id === id) : -1;
  const detailPanel = useMemo(
    () => id ? (
      <OrganizationDetailPanel
        id={id}
        embedded
        fallback={items[selectedIndex]}
        onClose={() => navigate('/organizations')}
        onPrevious={selectedIndex > 0 ? () => navigate(`/organizations/${items[selectedIndex - 1]!.id}`) : undefined}
        onNext={selectedIndex >= 0 && selectedIndex < items.length - 1 ? () => navigate(`/organizations/${items[selectedIndex + 1]!.id}`) : undefined}
      />
    ) : undefined,
    [id, items, navigate, selectedIndex],
  );
  const loadedRecord = useMemo<RightSidebarRecordDetails | undefined>(
    () => id && detailPanel ? {
      key: `organization:${id}`,
      recordId: id,
      recordTypeKey: 'organization',
      title: items[selectedIndex]?.name ?? id,
      content: detailPanel,
      sourcePath: '/organizations',
      onClear: () => navigate('/organizations'),
      canGoPrevious: selectedIndex > 0,
      canGoNext: selectedIndex >= 0 && selectedIndex < items.length - 1,
      onPrevious: () => navigate(`/organizations/${items[selectedIndex - 1]!.id}`),
      onNext: () => navigate(`/organizations/${items[selectedIndex + 1]!.id}`),
    } : undefined,
    [detailPanel, id, items, navigate, selectedIndex],
  );
  useLoadedRecordDetails(loadedRecord, Boolean(id));

  const pagination = (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={organizations.data?.total}
      count={items.length}
      onPageChange={setPage}
      busy={organizations.isFetching}
      label="organizations"
    />
  );

  // Below the split the detail replaces the list, so searching it from here
  // would be searching something that is no longer on screen.
  const listVisible = useIsSplitView() || !id;

  return (
    <div className="flex flex-col gap-4">
      {listVisible && (
        <ListToolbar
          search={<SearchField value={q} onChange={setQ} placeholder="Search organizations…" label="Search organizations" />}
          count={`${organizations.data?.total ?? items.length} organizations`}
          viewToggle={!id && <CollectionViewToggle view={view} onChange={setView} />}
        />
      )}
      {organizations.isLoading ? (
        <LoadingState />
      ) : organizations.isError ? (
        <ErrorState error={organizations.error} />
      ) : items.length === 0 ? (
        <EmptyState label="No organizations match." />
      ) : (
        <WorkspaceSplitView>
          {id ? (
            <CompactRecordList title={`${organizations.data?.total ?? items.length} organizations`}>
              {items.map((organization) => (
                <CompactRecordListItem
                  key={organization.id}
                  selected={organization.id === id}
                  title={organization.name}
                  subtitle={organization.industry ?? organization.website ?? 'No industry'}
                  meta={organization.status}
                  onSelect={() => navigate(`/organizations/${organization.id}`)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <div className="flex flex-col gap-4">
              <RecordCardGrid>
                {items.map((organization) => (
                  <RecordCard
                    key={organization.id}
                    recordType="organization"
                    icon={Building2}
                    title={organization.name}
                    subtitle={organization.industry ?? 'Organization'}
                    badges={<StatusBadge label={organization.status} />}
                    details={[
                      { label: 'Primary contact', value: organization.primaryContactName },
                      {
                        label: 'Phone',
                        value: organization.primaryContactPhone && (
                          <a className="text-primary hover:underline" href={`tel:${organization.primaryContactPhone}`}>
                            {organization.primaryContactPhone}
                          </a>
                        ),
                      },
                      {
                        label: 'Email',
                        value: (
                          <EmailLink
                            email={organization.primaryContactEmail}
                            name={organization.primaryContactName ?? organization.name}
                          />
                        ),
                      },
                      {
                        label: 'Last activity',
                        value: organization.lastActivityAt ? formatDateTime(organization.lastActivityAt) : undefined,
                      },
                    ]}
                    meta={
                      <>
                        <span>
                          {organization.contactCount} contact{organization.contactCount === 1 ? '' : 's'}
                          {' · '}
                          {organization.totalLeadCount} lead{organization.totalLeadCount === 1 ? '' : 's'}
                          {organization.openLeadCount > 0 && ` (${organization.openLeadCount} open)`}
                        </span>
                        <span>Added {formatDateTime(organization.createdAt)}</span>
                      </>
                    }
                    onOpen={() => navigate(`/organizations/${organization.id}`)}
                    openLabel="Open organization"
                  />
                ))}
              </RecordCardGrid>
              {pagination}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <RecordTable
                minWidth={960}
                columns={[
                  { key: 'org', header: 'Organization' },
                  { key: 'contact', header: 'Primary contact' },
                  { key: 'phone', header: 'Phone', hideOnMobile: true },
                  { key: 'email', header: 'Email', hideOnMobile: true },
                  { key: 'contacts', header: 'Contacts', align: 'right', hideOnMobile: true },
                  { key: 'leads', header: 'Leads', align: 'right' },
                  { key: 'status', header: 'Status' },
                  { key: 'activity', header: 'Last activity', hideOnMobile: true },
                  { key: 'added', header: 'Added', hideOnMobile: true },
                ]}
              >
                {items.map((organization) => (
                  <RecordTableRow
                    key={organization.id}
                    onOpen={() => navigate(`/organizations/${organization.id}`)}
                  >
                    <RecordTablePrimaryCell
                      title={organization.name}
                      subtitle={organization.industry}
                      width={200}
                    />
                    <RecordTableCell><Cell value={organization.primaryContactName} width="18ch" /></RecordTableCell>
                    <RecordTableCell hideOnMobile>
                      {organization.primaryContactPhone && (
                        <a
                          className="text-primary hover:underline"
                          href={`tel:${organization.primaryContactPhone}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {organization.primaryContactPhone}
                        </a>
                      )}
                    </RecordTableCell>
                    <RecordTableCell hideOnMobile>
                      <Cell
                        value={(
                          <EmailLink
                            email={organization.primaryContactEmail}
                            name={organization.primaryContactName ?? organization.name}
                          />
                        )}
                        width="22ch"
                      />
                    </RecordTableCell>
                    <RecordTableCell align="right" hideOnMobile>
                      <CountCell value={organization.contactCount} />
                    </RecordTableCell>
                    <RecordTableCell align="right">
                      <CountCell value={organization.totalLeadCount} />
                      {organization.openLeadCount > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">({organization.openLeadCount} open)</span>
                      )}
                    </RecordTableCell>
                    <RecordTableCell><StatusBadge label={organization.status} /></RecordTableCell>
                    <RecordTableCell hideOnMobile><DateCell value={organization.lastActivityAt} /></RecordTableCell>
                    <RecordTableCell hideOnMobile><DateCell value={organization.createdAt} /></RecordTableCell>
                  </RecordTableRow>
                ))}
              </RecordTable>
              {pagination}
            </div>
          )}
        </WorkspaceSplitView>
      )}
    </div>
  );
}

function OrganizationDetailPanel({
  id,
  fallback,
  onClose,
  onPrevious,
  onNext,
  embedded = false,
}: {
  id: string;
  fallback?: CascadeOrganizationListItem;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  embedded?: boolean;
}) {
  const [tab, setTab] = useState('overview');
  useEffect(() => setTab('overview'), [id]);
  const detail = useQuery({
    queryKey: ['organization', id],
    queryFn: () => leadloopApi.organizationDetail(id),
  });
  const activity = useQuery({
    queryKey: ['activity', id],
    queryFn: () => leadloopApi.activity({ entityId: id, limit: 100 }),
  });
  const organization = detail.data?.organization ?? fallback;

  return (
    <RecordDetailShell
      embedded={embedded}
      recordType="organization"
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      {detail.isLoading && !organization ? (
        <LoadingState />
      ) : detail.isError ? (
        <ErrorState error={detail.error} />
      ) : organization ? (
        <>
          <RecordDetailHeading
            icon={Building2}
            title={organization.name}
            badges={
              <>
                <StatusBadge label={organization.status} />
                {organization.industry && (
                  <span className="text-xs text-muted-foreground">{organization.industry}</span>
                )}
              </>
            }
          />
          <RecordTabs active={tab} onChange={setTab} tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'contacts', label: 'Contacts', count: detail.data?.contacts.length ?? 0 },
            { id: 'leads', label: 'Leads', count: detail.data?.leads.length ?? 0 },
            { id: 'submissions', label: 'Forms', count: detail.data?.submissions.length ?? 0 },
            { id: 'notes', label: 'Notes' },
            { id: 'activity', label: 'Activity' },
          ]} />
          <DetailTabBody>
          {tab === 'overview' && <>
          <DetailSection title="Organization details">
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field
                label="Website"
                value={
                  organization.website && (
                    <a
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      href={organization.website}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {organization.website}
                    </a>
                  )
                }
              />
              <Field
                label="Phone"
                value={
                  organization.phone && (
                    <a
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      href={`tel:${organization.phone}`}
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {organization.phone}
                    </a>
                  )
                }
              />
              <Field
                label="Email"
                value={
                  <EmailLink
                    email={organization.email}
                    name={organization.name}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" aria-hidden />
                    {organization.email}
                  </EmailLink>
                }
              />
              <Field label="Industry" value={organization.industry} />
              <Field label="Created" value={formatDateTime(organization.createdAt)} />
              <Field label="Updated" value={formatDateTime(organization.updatedAt)} />
            </dl>
          </DetailSection>
          {organization.notes && (
            <DetailSection title="Notes">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {organization.notes}
              </p>
            </DetailSection>
          )}
          </>}
          {tab === 'contacts' && <DetailSection title="Contacts"><RelatedRecordList empty="No contacts are linked to this organization." items={(detail.data?.contacts ?? []).map((contact) => ({ id: contact.id, to: `/contacts/${contact.id}`, title: contact.fullName, subtitle: contact.roleTitle ?? contact.email }))} /></DetailSection>}
          {tab === 'leads' && <DetailSection title="Leads"><RelatedRecordList empty="No leads are linked to this organization." items={(detail.data?.leads ?? []).map((lead) => ({ id: lead.id, to: `/leads/${lead.id}`, title: lead.title, subtitle: lead.contactName ?? lead.statusLabel, meta: formatDateTime(lead.createdAt) }))} /></DetailSection>}
          {tab === 'submissions' && <DetailSection title="Form submissions"><RelatedRecordList empty="No submissions are linked to this organization." items={(detail.data?.submissions ?? []).map((submission) => ({ id: submission.id, to: `/submissions/${submission.id}`, title: submission.formName, subtitle: submission.submittedName ?? submission.submittedEmail, meta: formatDateTime(submission.createdAt) }))} /></DetailSection>}
          {tab === 'notes' && <SimpleNotesPanel entityType="organization" entityId={organization.id} entityLabel={organization.name} activity={activity.data?.items ?? []} />}
          {tab === 'activity' && <DetailSection title="Activity and history"><ActivityTimeline items={activity.data?.items ?? []} /></DetailSection>}
          </DetailTabBody>
        </>
      ) : null}
    </RecordDetailShell>
  );
}
